import type { QuotaType } from '@/config/plans'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { useQuotaStore } from '@/stores/quotaStore'
import { useLocation } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo } from 'react'
import { resolveQuotaTenantId } from './resolveQuotaTenantId'

export type QuotaDataStatus = 'ok' | 'warning' | 'exceeded' | 'loading' | 'pending'

export interface UseQuotaResult {
	quotaKey: QuotaType
	/** Populated when numeric quota is known; `null` when usage is not yet in cache (see `status`). */
	used: number | null
	/** Populated when numeric quota is known; `null` when usage is not yet in cache. */
	total: number | null
	/** `null` when `used`/`total` are unknown. */
	percentage: number | null
	/**
	 * `loading` = fetch in flight for this tenant; `pending` = fetch finished but this quota is still absent
	 * (treat as unknown, not as zero). Otherwise numeric `ok` | `warning` | `exceeded`.
	 */
	status: QuotaDataStatus
	isLoading: boolean
	refetch: () => Promise<void>
}

export interface UseQuotaOptions {
	/** Fraction (0..1) at which status becomes 'warning'. Default 0.8. */
	threshold?: number
}

const DEFAULT_WARNING_THRESHOLD = 0.8

/**
 * Subscribes to a single quota slice for the current tenant.
 * Triggers a cached fetch on mount; text-based quotas short-circuit to 'ok'.
 *
 * When the numeric slice is missing from the store, `used` / `total` are `null` and `status` is
 * `loading` (fetch in flight) or `pending` (fetched but this key still absent) — not a real zero
 * allocation.
 *
 * When `quotaKey` is undefined, returns a no-op result (used by useAccessControl
 * so callers don't need conditional hook invocation).
 */
export function useQuota(quotaKey: QuotaType | undefined, options: UseQuotaOptions = {}): UseQuotaResult | null {
	const threshold = options.threshold ?? DEFAULT_WARNING_THRESHOLD
	const { pathname } = useLocation()
	const currentTenant = useAuthStore(s => s.currentTenant)
	const organizationId = useAuthStore(s => s.currentOrganizationId)
	const user = useAuthStore(s => s.user)
	const rootTenantId = useProjectsStore(s => s.rootTenantId)
	const tenantId = useMemo(
		() =>
			resolveQuotaTenantId({
				currentTenant,
				pathname,
				rootTenantId,
				organizationId,
				user,
			}),
		[currentTenant, pathname, rootTenantId, organizationId, user],
	)
	const value = useQuotaStore(s => (quotaKey && tenantId ? s.quotaUsage[tenantId]?.quotas[quotaKey] : undefined))
	const isLoading = useQuotaStore(s => s.loading && tenantId != null && !s.quotaUsage?.[tenantId])
	const fetchQuotaUsage = useQuotaStore(s => s.fetchQuotaUsage)

	useEffect(() => {
		if (!quotaKey || !tenantId) {
			return
		}
		fetchQuotaUsage(tenantId).catch(() => {
			// Errors surface via useQuotaStore.error; consumer can inspect if needed.
		})
	}, [quotaKey, tenantId, fetchQuotaUsage])

	const refetch = useCallback(async () => {
		if (!tenantId) {
			return
		}
		await useQuotaStore.getState().refreshQuotaUsage(tenantId)
	}, [tenantId])

	if (!quotaKey) {
		return null
	}

	// Text-valued quotas (e.g. TTL) don't have numeric limits.
	if (value && 'value' in value) {
		return {
			quotaKey,
			used: 0,
			total: Number.POSITIVE_INFINITY,
			percentage: 0,
			status: 'ok',
			isLoading: false,
			refetch,
		}
	}

	if (!value) {
		return {
			quotaKey,
			used: null,
			total: null,
			percentage: null,
			status: isLoading ? 'loading' : 'pending',
			isLoading,
			refetch,
		}
	}

	const { used, total } = value
	const percentage = total > 0 ? (used / total) * 100 : 0
	const status: 'ok' | 'warning' | 'exceeded' =
		used >= total && total > 0 ? 'exceeded' : percentage >= threshold * 100 ? 'warning' : 'ok'

	return { quotaKey, used, total, percentage, status, isLoading: false, refetch }
}
